import React, { useState } from 'react';
import { useProducts } from '../contexts/ProductContext';
import { useAuth } from '../contexts/AuthContext';
import { useCategories } from '../contexts/CategoryContext';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaSave, FaTimes } from 'react-icons/fa';

const AdminProducts = () => {
  const { products, addProduct, updateProduct, deleteProduct, toggleProductAvailability } = useProducts();
  const { isAdmin } = useAuth();
  const { categories, addCategory, editCategory, deleteCategory } = useCategories();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image: ''
  });
  // Category management state
  const [newCategory, setNewCategory] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');

  if (!isAdmin) {
    return (
      <div className="container text-center mt-5">
        <h2>Access Denied</h2>
        <p>You need admin privileges to access this page.</p>
      </div>
    );
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const productData = {
      ...formData,
      price: parseFloat(formData.price)
    };
    if (editingProduct) {
      await updateProduct(editingProduct.id, productData);
      setEditingProduct(null);
    } else {
      await addProduct(productData);
    }
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      image: ''
    });
    setShowAddForm(false);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image: product.image
    });
    setShowAddForm(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      await deleteProduct(productId);
    }
  };

  const handleToggleAvailability = async (productId) => {
    await toggleProductAvailability(productId);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      image: ''
    });
    setEditingProduct(null);
    setShowAddForm(false);
  };

  // Category management handlers
  const handleAddCategory = (e) => {
    e.preventDefault();
    if (newCategory.trim()) {
      addCategory(newCategory.trim());
      setNewCategory('');
    }
  };
  const handleEditCategory = (id, name) => {
    setEditingCategoryId(id);
    setEditingCategoryName(name);
  };
  const handleSaveCategory = (id) => {
    if (editingCategoryName.trim()) {
      editCategory(id, editingCategoryName.trim());
      setEditingCategoryId(null);
      setEditingCategoryName('');
    }
  };
  const handleCancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryName('');
  };
  const handleDeleteCategory = (id) => {
    deleteCategory(id);
  };
  const isProtectedCategory = (name) => ['Veg', 'NonVeg'].includes(name);

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Manage Products</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="btn btn-primary"
        >
          <FaPlus className="me-2" />
          Add New Product
        </button>
      </div>

      {/* Category Management Section */}
      <div className="card mb-4">
        <div className="card-body">
          <h4 className="card-title">Manage Categories</h4>
          <form className="d-flex gap-2 mb-3" onSubmit={handleAddCategory}>
            <input
              type="text"
              className="form-control"
              placeholder="Add new category"
              value={newCategory}
              onChange={e => setNewCategory(e.target.value)}
              maxLength={20}
            />
            <button type="submit" className="btn btn-success">
              <FaPlus />
            </button>
          </form>
          <ul className="list-group">
            {categories.map(cat => (
              <li key={cat.id} className="list-group-item d-flex align-items-center justify-content-between">
                {editingCategoryId === cat.id ? (
                  <>
                    <input
                      type="text"
                      className="form-control me-2"
                      value={editingCategoryName}
                      onChange={e => setEditingCategoryName(e.target.value)}
                      maxLength={20}
                    />
                    <button className="btn btn-success btn-sm me-1" onClick={() => handleSaveCategory(cat.id)}>
                      <FaSave />
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={handleCancelEditCategory}>
                      <FaTimes />
                    </button>
                  </>
                ) : (
                  <>
                    <span>{cat.name}</span>
                    <div>
                      <button
                        className="btn btn-primary btn-sm me-1"
                        onClick={() => handleEditCategory(cat.id, cat.name)}
                        disabled={isProtectedCategory(cat.name)}
                        title={isProtectedCategory(cat.name) ? 'Cannot edit default category' : 'Edit'}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteCategory(cat.id)}
                        disabled={isProtectedCategory(cat.name)}
                        title={isProtectedCategory(cat.name) ? 'Cannot delete default category' : 'Delete'}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {showAddForm && (
        <div className="card mb-4">
          <div className="card-body">
            <h4 className="card-title">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h4>
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="name">Product Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="category">Category</label>
                    <select
                      id="category"
                      name="category"
                      className="form-control"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  required
                />
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="price">Price (₹)</label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      className="form-control"
                      value={formData.price}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="image">Image URL</label>
                    <input
                      type="url"
                      id="image"
                      name="image"
                      className="form-control"
                      value={formData.image}
                      onChange={handleInputChange}
                      placeholder="https://example.com/image.jpg"
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary">
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="product-grid">
        {products.map((product) => (
          <div key={product.id} className="product-card">
            <img
              src={product.image}
              alt={product.name}
              className="product-image"
            />
            <div className="product-info">
              <h3 className="product-title">{product.name}</h3>
                                <p className="product-price">₹{product.price}</p>
              <p className="product-description">{product.description}</p>
              <div className="d-flex gap-2 mb-2">
                <span className={`badge ${product.available ? 'bg-success' : 'bg-secondary'}`}>
                  {product.available ? 'Available' : 'Unavailable'}
                </span>
                <span className="badge bg-info">{product.category}</span>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <button
                  onClick={() => handleEdit(product)}
                  className="btn btn-primary btn-sm"
                >
                  <FaEdit className="me-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleToggleAvailability(product.id)}
                  className={`btn btn-sm ${product.available ? 'btn-warning' : 'btn-success'}`}
                >
                  {product.available ? (
                    <>
                      <FaEyeSlash className="me-1" />
                      Hide
                    </>
                  ) : (
                    <>
                      <FaEye className="me-1" />
                      Show
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  className="btn btn-danger btn-sm"
                >
                  <FaTrash className="me-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {products.length === 0 && (
        <div className="text-center mt-5">
          <h3>No products found</h3>
          <p>Add your first product to get started!</p>
        </div>
      )}
    </div>
  );
};

export default AdminProducts; 